import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Boxes, List, Plus } from "lucide-react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import { TextInput } from "../../../ui/components/Form";
import { ListView } from "../../../ui/components/ListView";
import type { ListViewContextAction } from "../../../ui/components/ListView";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import type { ListParams, ListResult } from "../../../ui/api/listApi.types";
import { useAuth } from "../../lib/auth/hooks";
import { useI18n } from "../../lib/i18n";
import { authenticatedProApiFetch } from "../../lib/api/pro-api.client";
import type { DataCatalog } from "../../lib/api/products";
import { useListImportExport } from "../data-transfer/useListImportExport";
import type { ExportPreviewItem } from "../data-transfer/useListImportExport";
import * as pageStyles from "../users/UsersListPage.css";

function createCatalogColumns(t: (key: string) => string): ColumnDefinition<DataCatalog>[] {
  return [
    {
      id: "name",
      label: t("pro.system.dataCatalog.col.name"),
      render: (row) => (
        <span style={{ fontWeight: 600 }}>
          {row.name}
          {!row.isActive && (
            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, opacity: 0.5 }}>
              {t("pro.system.dataCatalog.badge.inactive")}
            </span>
          )}
        </span>
      ),
    },
    {
      id: "description",
      label: t("pro.system.dataCatalog.col.description"),
      render: (row) => (
        <span style={{ color: "var(--color-text-secondary, #6B7280)" }}>
          {row.description ?? "—"}
        </span>
      ),
    },
    {
      id: "slug",
      label: t("pro.system.dataCatalog.col.slug"),
      render: (row) => (
        <code style={{ fontFamily: "monospace", fontSize: 12, opacity: 0.65 }}>
          {row.slug ?? "—"}
        </code>
      ),
    },
    {
      id: "firmwareVersion",
      label: t("pro.system.dataCatalog.col.firmware"),
      render: (row) => row.firmwareVersion ?? "—",
    },
  ];
}

export function DataCatalogListPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { accessToken } = useAuth();
  const token = accessToken ?? localStorage.getItem("access_token");

  const [catalogs, setCatalogs] = React.useState<DataCatalog[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [createName, setCreateName] = React.useState("");
  const [createDescription, setCreateDescription] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);

  const catalogColumns = React.useMemo(() => createCatalogColumns(t), [t]);

  const load = React.useCallback(() => {
    if (!token) return;
    setLoading(true);
    authenticatedProApiFetch<{ data: DataCatalog[] }>("/data-catalogs", token)
      .then((res) => setCatalogs(res.data))
      .catch((err) => setError(err?.message || t("pro.system.dataCatalog.error.load")))
      .finally(() => setLoading(false));
  }, [token, t]);

  React.useEffect(() => { load(); }, [load]);

  const exportPreviewItems = React.useMemo((): ExportPreviewItem[] => {
    return catalogs.map((c) => ({
      id: c.id,
      label: c.name,
      matchKey: c.slug ?? c.name,
    }));
  }, [catalogs]);

  const {
    headerActions: importExportHeaderActions,
    exportContextAction,
    modals: importExportModals,
  } = useListImportExport({
    entity: "data-catalogs",
    entityLabel: "Data catalogs",
    enableImport: true,
    enableExport: true,
    enableCheckpoints: false,
    totalCount: catalogs.length,
    selectedIds,
    previewItems: exportPreviewItems,
    defaultFormat: "json",
    onImported: load,
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !createName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await authenticatedProApiFetch<DataCatalog>("/data-catalogs", token, {
        method: "POST",
        body: JSON.stringify({ name: createName.trim(), description: createDescription.trim() || null }),
      });
      setCreateOpen(false);
      setCreateName("");
      setCreateDescription("");
      navigate({ to: "/system/data-catalog/$catalogId", params: { catalogId: created.id } });
    } catch (err: any) {
      setCreateError(err?.message || t("pro.system.dataCatalog.error.create"));
    } finally {
      setCreating(false);
    }
  };

  const fetchCatalogList = React.useCallback(
    (params: ListParams): Promise<ListResult<DataCatalog>> => {
      const search = params.search?.toLowerCase();
      const filtered = search
        ? catalogs.filter(
            (c) =>
              c.name.toLowerCase().includes(search) ||
              c.slug?.toLowerCase().includes(search) ||
              c.description?.toLowerCase().includes(search),
          )
        : catalogs;
      const start = (params.page - 1) * params.limit;
      return Promise.resolve({
        data: filtered.slice(start, start + params.limit),
        total: filtered.length,
      });
    },
    [catalogs],
  );

  const getCatalogContextActions = React.useCallback(
    (row: DataCatalog): ListViewContextAction[] => {
      const actions: ListViewContextAction[] = [
        {
          id: "edit",
          label: t("shared.common.edit"),
          onClick: () =>
            navigate({ to: "/system/data-catalog/$catalogId", params: { catalogId: row.id } }),
        },
      ];
      if (exportContextAction) {
        actions.push({
          id: exportContextAction.id,
          label: exportContextAction.label,
          icon: exportContextAction.icon,
          bulk: true,
          onClick: (ids) => exportContextAction.onClick(ids?.length ? ids : [row.id]),
        });
      }
      return actions;
    },
    [navigate, t, exportContextAction],
  );

  const handleRowClick = React.useCallback(
    (row: DataCatalog) => {
      navigate({ to: "/system/data-catalog/$catalogId", params: { catalogId: row.id } });
    },
    [navigate],
  );

  const headerActions = React.useMemo(
    () => [
      {
        id: "new-catalog",
        label: t("pro.system.dataCatalog.newCatalog"),
        icon: <Plus size={14} />,
        color: "primary" as const,
        onClick: () => setCreateOpen(true),
      },
      {
        id: "enum-groups",
        label: t("pro.system.dataCatalog.enumGroups"),
        icon: <List size={14} />,
        color: "neutral" as const,
        variant: "outline" as const,
        onClick: () => navigate({ to: "/system/data-catalog/enum-groups" }),
      },
      {
        id: "product-models",
        label: t("pro.system.dataCatalog.productModels"),
        icon: <Boxes size={14} />,
        color: "neutral" as const,
        variant: "outline" as const,
        onClick: () => navigate({ to: "/system/data-catalog/product-models" }),
      },
      ...importExportHeaderActions,
    ],
    [t, navigate, importExportHeaderActions],
  );

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.pageHeader}>
        <div>
          <Text level="h1" color="text" weight="bold">
            {t("pro.system.dataCatalog.title")}
          </Text>
          <Text level="body-medium" style={{ color: "#6B7280", marginTop: 4 }}>
            {t("pro.system.dataCatalog.subtitle")}
          </Text>
        </div>
      </div>

      {error && (
        <div className={pageStyles.errorWrap}>
          <Callout variant="error">{error}</Callout>
        </div>
      )}

      <div className={pageStyles.tableHost}>
        {!loading && (
          <ListView
            key={catalogs.length}
            listId="data-catalog-list"
            columns={catalogColumns}
            fetchList={fetchCatalogList}
            getContextActions={getCatalogContextActions}
            onRowClick={handleRowClick}
            onSelectionChange={setSelectedIds}
            enableSelection
            compact
            fillContainer
            enabledViews={["table"]}
            disableLocalPersistence
            searchPlaceholder={t("pro.system.dataCatalog.searchPlaceholder")}
            emptyTitle={t("pro.system.dataCatalog.emptyTitle")}
            emptySubtitle={t("pro.system.dataCatalog.emptySubtitle")}
            headerActions={headerActions}
          />
        )}
      </div>

      {importExportModals}

      <Modal open={createOpen} setOpen={setCreateOpen} minWidth="420px" maxWidth="500px" closeOnClickOff>
        <div style={{ padding: "24px 28px" }}>
          <Text level="h2" weight="bold" style={{ marginBottom: 20 }}>
            {t("pro.system.dataCatalog.createModalTitle")}
          </Text>
          <form onSubmit={handleCreate}>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <TextInput
                label={t("pro.system.dataCatalog.form.nameRequired")}
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder={t("pro.system.dataCatalog.form.namePlaceholder")}
                disabled={creating}
                autoFocus
              />
              <TextInput
                label={t("pro.system.dataCatalog.form.description")}
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                placeholder={t("pro.system.dataCatalog.form.descriptionPlaceholder")}
                disabled={creating}
              />
            </div>
            {createError && (
              <div style={{ marginTop: 14 }}>
                <Callout variant="error">{createError}</Callout>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
              <Button
                type="button"
                color="neutral"
                onClick={() => setCreateOpen(false)}
                disabled={creating}
              >
                {t("shared.common.cancel")}
              </Button>
              <Button
                type="submit"
                color="primary"
                disabled={creating || !createName.trim()}
              >
                {creating ? t("pro.system.dataCatalog.creating") : t("pro.system.dataCatalog.create")}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}
